import { Waken } from "@components/Waken";
import { MavenService } from "@maven-rest/common/common.types";
import { AuthTokenFlax } from "@maven-rest/sso/AuthToken.flax";
import { store } from "@maven-rest/states";
import { sessionSlice } from "@maven-rest/states/reducers/session";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useRequest } from "@coralblack/flax";
import { CrDialog } from "@maven-surface/components/base/CrDialog";
import { CrTimer } from "@maven-surface/components/base/CrTimer";
import { useInterval } from "@maven-surface/hooks";
import { AUTH_USER_LANG } from "@maven-msg/lang/auth/user";
import { MvnMessage } from "@maven-msg/lib/Message";
import dayjs from "dayjs";

const HEART_BEAT_INTERVAL = 1000 * 60; // 1m
const TOKEN_REFRESH_ALERT_CRITERIA_SECS = 60 * 15; // 15m

export function TokenManager() {
  const isTokenAwakeDisabled = useRef<boolean>(false);
  const isTokenRefreshDisabled = useRef<boolean>(false);

  const refreshedAt = useRef<number>(new Date().getTime());
  const alarmedAt = useRef<number>(0);

  const [remainSecs, setRemainSecs] = useState(0);
  const [tokenRefreshAlarmDialogVisibility, setTokenRefreshAlarmDialogVisibility] = useState(false);

  const { request: awakeAuthTokenRequest } = useRequest(AuthTokenFlax.awakeAuthTokenForTest({ service: MavenService.MavenECoa }), {
    success: (resp) => {
      dispatch(sessionSlice.actions.updateExpiryAt({ ...resp }));
    },
    done: () => {
      isTokenAwakeDisabled.current = false;
    },
  });

  useInterval(() => {
    if (!isTokenAwakeDisabled.current) {
      isTokenAwakeDisabled.current = true;
      awakeAuthTokenRequest();
    }
  }, HEART_BEAT_INTERVAL);

  useEffect(() => {
    awakeAuthTokenRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatch = useDispatch();
  const signOut = () => dispatch(sessionSlice.actions.signOut());

  const { request: authTokenRefreshRequest, response: authTokenRefreshResponse } = useRequest(
    AuthTokenFlax.refreshAuthTokenForTest({ service: MavenService.MavenECoa }),
    {
      success: (resp) => {
        isTokenRefreshDisabled.current = true;
        setTimeout(() => {
          isTokenRefreshDisabled.current = false;
        }, 10 * 1000);

        dispatch(sessionSlice.actions.refreshSignIn({ ...resp }));
        refreshedAt.current = new Date().getTime();

        return MvnMessage(AUTH_USER_LANG.USER_TOKEN_REFRESH_SUCC);
      },
      error: (error) => {
        if (error?.code === "CREDENTIAL-INVALID") {
          signOut();
        }

        return MvnMessage(AUTH_USER_LANG.USER_TOKEN_REFRESH_FAIL, { error: error?.message });
      },
    }
  );

  return (
    <>
      <CrDialog
        header="Maven Clinical Cloud"
        visibility={tokenRefreshAlarmDialogVisibility}
        onConfirm={() => {
          authTokenRefreshRequest();
          setTokenRefreshAlarmDialogVisibility(!tokenRefreshAlarmDialogVisibility);
          alarmedAt.current = refreshedAt.current;
        }}
        onCancel={() => {
          setTokenRefreshAlarmDialogVisibility(!tokenRefreshAlarmDialogVisibility);
          alarmedAt.current = refreshedAt.current;
        }}
        confirm="Extend"
        cancel="Cancel"
      >
        {MvnMessage(AUTH_USER_LANG.USER_TOKEN_EXPIRY_ALERT, { minutes: Math.floor(remainSecs / 60), seconds: remainSecs % 60 })}
        <br />
        {MvnMessage(AUTH_USER_LANG.USER_TOKEN_EXTEND_CONFIRM)}
      </CrDialog>
      <CrTimer
        title={MvnMessage(AUTH_USER_LANG.USER_TOKEN_EXTEND_BTN_DESC)}
        busy={authTokenRefreshResponse.busy}
        disabled={isTokenRefreshDisabled.current}
        remainSeconds={() => dayjs(String(store.getState().session.expiryAt)).diff(dayjs(), "second")}
        onClick={() => {
          if (isTokenRefreshDisabled.current) return;

          authTokenRefreshRequest();
        }}
        onTick={(secs) => {
          setRemainSecs(secs);

          if (refreshedAt.current !== alarmedAt.current) {
            setTokenRefreshAlarmDialogVisibility(secs > 0 && secs <= TOKEN_REFRESH_ALERT_CRITERIA_SECS);
          }

          if (secs <= 0) {
            signOut();
          }
        }}
      />
      <Waken />
    </>
  );
}
